import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiAward,
  FiBookOpen,
  FiShield,
  FiCheckCircle,
  FiUsers,
} from "react-icons/fi";
import Layout from "../../components/Layout";
import TrainingHero from "./TrainingHero";
import TrainingStandardsSection from "./TrainingStandardsSection";
import { TRAINING_PAGE_STANDARDS } from "../../data/trainingData";

const SITE_URL = "https://mbasolutions.co";

const levels = [
  {
    level: "HACCP Level 2",
    short: "Foundation Level",
    icon: FiBookOpen,
    description:
      "HACCP Level 2 is designed for food handlers and operational staff who are directly involved in food preparation, processing, storage, and distribution.",
    points: [
      "Introduction to food safety hazards",
      "Basic HACCP principles",
      "Personal hygiene & contamination control",
      "Safe food handling practices",
      "Legal responsibilities",
    ],
  },
  {
    level: "HACCP Level 3",
    short: "Supervisory Level",
    icon: FiShield,
    description:
      "HACCP Level 3 targets supervisors, quality officers, and team leaders responsible for implementing and monitoring food safety systems.",
    points: [
      "Detailed hazard analysis",
      "Developing HACCP plans",
      "Monitoring & verification procedures",
      "Corrective actions",
      "Food safety management documentation",
    ],
  },
  {
    level: "HACCP Level 4",
    short: "Management Level",
    icon: FiUsers,
    description:
      "HACCP Level 4 is for senior management, consultants, and auditors who design, review, and continuously improve HACCP systems.",
    points: [
      "Advanced HACCP system design",
      "Risk-based decision making",
      "Integration with ISO 22000 & FSMS",
      "Audit readiness & compliance",
      "Leadership in food safety culture",
    ],
  },
];

const HaccpTraining = () => {
  return (
    <Layout
      title="HACCP Level 2 / 3 / 4 Training | MBA Solutions"
      description="MBA Solutions provides internationally recognized HACCP Level 2, 3, and 4 training programs tailored to food handlers, supervisors, and management."
      keywords="HACCP Level 2, HACCP Level 3, HACCP Level 4, food safety training, HACCP certification, MBA Solutions"
      canonical={`${SITE_URL}/haccp-training`}
    >
      <TrainingHero
        badge="Food Safety Certification"
        title="HACCP Certification Levels"
        subtitle="Internationally recognized HACCP training programs tailored to different organizational roles and responsibilities."
        icon={FiAward}
        primaryCta={{ label: "Book HACCP Training", to: "/contact" }}
        secondaryCta={{ label: "All Training", to: "/training" }}
      />

      <TrainingStandardsSection {...TRAINING_PAGE_STANDARDS.foodSafety} />

      <section className="bg-gray-50 py-14 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--bg-primary)] mb-4">
              HACCP Certification Levels
            </h2>
            <div
              className="w-20 h-1 bg-[#F86624] mx-auto rounded-full mb-4"
              aria-hidden="true"
            />
            <p className="text-gray-600 text-base md:text-lg">
              MBA Solutions provides internationally recognized HACCP training
              programs tailored to different organizational roles and
              responsibilities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {levels.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.level}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  whileHover={{ y: -10, scale: 1.02 }}
                  className="relative bg-white rounded-3xl p-8 shadow-xl border border-gray-100 group"
                >
                  <span className="absolute top-6 right-6 text-xs font-semibold px-3 py-1 rounded-full bg-[var(--bg-primary)] text-white shadow">
                    {item.short}
                  </span>
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-[var(--bg-primary)] text-white mb-6 shadow-lg">
                    <Icon size={32} aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[var(--bg-primary)] transition-colors">
                    {item.level}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6">
                    {item.description}
                  </p>
                  <ul className="space-y-3 text-sm text-gray-700 list-none p-0 m-0">
                    {item.points.map((point) => (
                      <li key={point} className="flex items-start gap-2">
                        <FiCheckCircle
                          size={16}
                          className="text-[var(--bg-primary)] mt-1 shrink-0"
                          aria-hidden="true"
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20 text-center"
          >
            <p className="text-gray-600 mb-6">
              Want to enroll your team in HACCP certification training?
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-3 rounded-full bg-[var(--bg-primary)] text-white font-semibold shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book HACCP Training
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default HaccpTraining;
